import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  CameraIcon,
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
  PhoneIcon,
  SparklesIcon,
  UserCircleIcon,
  UserGroupIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getSchoolProfile, updateSchoolProfile } from '../api/schoolApi';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://api.pawaschool.net/api/v1';
const STATIC_BASE_URL = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

const ADMIN_ROLES = ['admin', 'school_admin', 'super_admin'];

const ROLE_LABELS = {
  admin: 'School Administrator',
  school_admin: 'School Administrator',
  super_admin: 'Super Administrator',
  student: 'Student',
  parent: 'Parent',
  teacher: 'Teacher',
  library_user: 'Library User',
};

const SkeletonLoader = ({ adminView }) => (
  <div className="animate-pulse space-y-6">
    <div className="flex flex-col items-center">
      <div
        className={`${
          adminView ? 'h-32 w-48' : 'h-24 w-24 rounded-full'
        } bg-gray-200 mb-4`}
      />
      <div className="h-6 w-40 rounded bg-gray-200" />
      <div className="mt-2 h-4 w-28 rounded bg-gray-200" />
    </div>
    <div className="space-y-3">
      <div className="h-20 rounded-xl bg-gray-200" />
      <div className="h-20 rounded-xl bg-gray-200" />
    </div>
  </div>
);

const DetailRow = ({ icon: Icon, label, value }) => {
  if (value === undefined || value === null || value === '') return null;

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="mt-0.5 rounded-lg bg-primary-50 p-2 text-primary-600">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="break-words text-sm font-medium text-gray-800">{value}</p>
      </div>
    </div>
  );
};

const ProfileDrawer = ({ isOpen, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const role = String(user?.role || '').toLowerCase();
  const isAdmin = ADMIN_ROLES.includes(role);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [school, setSchool] = useState(null);
  const [cachedSchool, setCachedSchool] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [personalImageFailed, setPersonalImageFailed] = useState(false);
  const [teacherProfile, setTeacherProfile] = useState(null);
  const fileInputRef = useRef(null);

  const getLogoUrl = (data) => data?.logoUrl || data?.logo_url || '';

  const buildImageUrl = (path) => {
    if (!path) return '';
    if (/^(https?:|data:|blob:)/i.test(path)) return path;
    return `${STATIC_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  };

  const fetchSchool = async (silent = false) => {
    if (!silent) setLoading(true);

    try {
      const res = await getSchoolProfile();
      if (res?.success) {
        setSchool(res.data);
        setCachedSchool(res.data);
        setLogoPreview(getLogoUrl(res.data));
        return res.data;
      }
      return null;
    } catch (error) {
      if (!silent) {
        toast.error(
          error.response?.data?.message || 'Failed to load school profile'
        );
      }
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    // Only administrators are allowed to request school/subscription details.
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    if (cachedSchool) {
      setSchool(cachedSchool);
      setLogoPreview(getLogoUrl(cachedSchool));
      fetchSchool(true);
    } else {
      fetchSchool(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAdmin]);

  useEffect(() => {
    let active = true;

    const fetchTeacherProfile = async () => {
      if (!isOpen || role !== 'teacher') return;

      setLoading(true);
      try {
        const response = await apiClient.get('/teachers/me');
        if (active && response?.data?.data) {
          setTeacherProfile(response.data.data);
        }
      } catch (error) {
        console.error('Failed to load teacher profile:', error);
        if (active) {
          toast.error(
            error.response?.data?.message || 'Failed to load teacher profile'
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchTeacherProfile();
    return () => {
      active = false;
    };
  }, [isOpen, role]);

  useEffect(() => {
    if (isOpen) setPersonalImageFailed(false);
  }, [isOpen, user]);

  const handleFileChange = async (event) => {
    if (!isAdmin) return;

    const file = event.target.files?.[0];
    if (!file) return;

    const previousLogo = getLogoUrl(school);
    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await updateSchoolProfile(formData);

      if (res?.success) {
        const freshData = await fetchSchool(true);
        toast.success('School logo updated successfully');
        onUpdate?.(freshData);
      }
    } catch (error) {
      setLogoPreview(previousLogo);
      toast.error(error.response?.data?.message || 'Logo upload failed');
    } finally {
      URL.revokeObjectURL(previewUrl);
      setSaving(false);
      event.target.value = '';
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status, isValid) => {
    if (!status) return null;
    if (status === 'active' && isValid) {
      return {
        icon: CheckCircleIcon,
        label: 'Active',
        className: 'bg-green-100 text-green-800 border-green-200',
      };
    }
    if (status === 'trial' && isValid) {
      return {
        icon: ClockIcon,
        label: 'Trial',
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      };
    }
    if (!isValid && ['active', 'trial'].includes(status)) {
      return {
        icon: XCircleIcon,
        label: 'Expired',
        className: 'bg-red-100 text-red-800 border-red-200',
      };
    }
    return {
      icon: XCircleIcon,
      label: 'Inactive',
      className: 'bg-gray-100 text-gray-600 border-gray-200',
    };
  };

  const handlePricing = () => {
    onClose();
    navigate('/app/pricing');
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
      navigate('/', { replace: true });
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Logout failed. Please try again.');
    }
  };

  const openStudentProfile = () => {
    if (role !== 'student') return;
    onClose();
    navigate('/app/profile/student');
  };

  if (!isOpen) return null;

  const profile =
    (role === 'teacher' && teacherProfile) ||
    user?.student ||
    user?.teacher ||
    user?.parent ||
    user?.profile ||
    {};
  const firstName =
    profile.first_name || profile.firstName || user?.first_name || user?.firstName || '';
  const lastName =
    profile.last_name || profile.lastName || user?.last_name || user?.lastName || '';
  const fullName =
    profile.full_name ||
    profile.fullName ||
    user?.full_name ||
    user?.fullName ||
    `${firstName} ${lastName}`.trim() ||
    user?.name ||
    user?.username ||
    'User';
  const initials =
    fullName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'U';
  const personalPhoto =
    profile.photo_url ||
    profile.photoUrl ||
    profile.profile_picture ||
    profile.profilePicture ||
    profile.teacherUser?.profile_picture ||
    profile.teacherUser?.profilePicture ||
    user?.photo_url ||
    user?.photoUrl ||
    user?.profile_picture ||
    user?.avatar ||
    '';
  const personalPhotoSrc = buildImageUrl(personalPhoto);
  const className =
    profile.class_name ||
    profile.className ||
    profile.class?.name ||
    profile.Class?.name ||
    user?.class_name ||
    user?.className;
  const relationship =
    profile.relationship || user?.relationship || user?.parent_relationship;
  const teacherClasses = Array.isArray(profile.classes)
    ? profile.classes
    : Array.isArray(profile.Classes)
      ? profile.Classes
      : [];
  const teacherClassNames = teacherClasses
    .map((classItem) => classItem?.name)
    .filter(Boolean);
  const classTeacherClassId =
    profile.class_teacher_class_id ||
    profile.classTeacherClassId ||
    profile.current_class_id ||
    profile.currentClassId;
  const classTeacherClass =
    teacherClasses.find(
      (classItem) => Number(classItem?.id) === Number(classTeacherClassId)
    ) ||
    profile.currentClass ||
    profile.current_class ||
    null;
  const yearsOfExperience =
    profile.years_of_experience ??
    profile.yearsOfExperience ??
    0;

  const subscription = school?.subscription;
  const hasActive = Boolean(school?.hasActiveSubscription);
  const plan = subscription?.plan;
  const statusBadge = getStatusBadge(
    subscription?.status,
    subscription?.isValid
  );
  const StatusIcon = statusBadge?.icon;
  const imageSrc = buildImageUrl(logoPreview);

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className="fixed right-0 top-0 z-50 h-full w-full max-w-md bg-white shadow-2xl"
        aria-label={isAdmin ? 'School profile' : 'My profile'}
      >
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-xl font-semibold text-primary-700">
            {isAdmin ? 'School Profile' : 'My Profile'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 hover:bg-gray-100"
            aria-label="Close profile"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="h-[calc(100%-73px)] overflow-y-auto p-6">
          {loading ? (
            <SkeletonLoader adminView={isAdmin} />
          ) : isAdmin ? (
            <div className="space-y-8">
              <div className="flex flex-col items-center space-y-4">
                <div className="group relative w-full max-w-xs">
                  <div className="aspect-video w-full overflow-hidden rounded-xl bg-gray-100 shadow-sm">
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={`${school?.schoolName || school?.name || 'School'} logo`}
                        className="h-full w-full object-contain"
                        onError={() => setLogoPreview('')}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-gray-400">
                        <CameraIcon className="h-16 w-16" />
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                    className="absolute bottom-2 right-2 rounded-full bg-primary-500 p-2 text-white shadow-lg hover:bg-primary-600 disabled:opacity-50"
                    aria-label="Change school logo"
                  >
                    {saving ? (
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    ) : (
                      <CameraIcon className="h-5 w-5" />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                  />
                </div>

                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-800">
                    {school?.schoolName || school?.name || 'School Name'}
                  </h3>
                  {school?.address && (
                    <p className="mt-1 text-sm text-gray-600">{school.address}</p>
                  )}
                </div>
              </div>

              <div className="border-t pt-6">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700">
                    Subscription Plan
                  </h4>
                  {statusBadge && StatusIcon && (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${statusBadge.className}`}
                    >
                      <StatusIcon className="h-4 w-4" />
                      {statusBadge.label}
                    </span>
                  )}
                </div>

                {subscription && plan ? (
                  <div className="space-y-3 rounded-xl bg-gray-50 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-semibold text-gray-800">{plan.name}</p>
                        {plan.description && (
                          <p className="mt-0.5 text-xs text-gray-500">
                            {plan.description}
                          </p>
                        )}
                      </div>
                      {plan.price !== undefined && plan.price !== null && (
                        <span className="whitespace-nowrap text-sm font-bold text-primary-600">
                          {plan.currency && `${plan.currency} `}
                          {plan.price}
                        </span>
                      )}
                    </div>

                    {Array.isArray(plan.features) && (
                      <div className="flex flex-wrap gap-1">
                        {plan.features.map((feature, index) => (
                          <span
                            key={`${feature}-${index}`}
                            className="rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 border-t border-gray-200 pt-2 text-xs text-gray-600">
                      <div>
                        <span className="text-gray-400">Started:</span>{' '}
                        {formatDate(subscription.start_date)}
                      </div>
                      <div>
                        <span className="text-gray-400">Expires:</span>{' '}
                        {formatDate(
                          subscription.status === 'trial'
                            ? subscription.trial_ends_at
                            : subscription.end_date
                        )}
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-400">Auto-renew:</span>{' '}
                        {subscription.auto_renew || subscription.auto_renewal
                          ? 'Yes'
                          : 'No'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl bg-yellow-50 p-4 text-center">
                    <p className="text-sm text-gray-600">
                      No active subscription found.
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Subscribe to unlock full features.
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handlePricing}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary-700 active:scale-[0.97]"
                >
                  {subscription && hasActive ? (
                    <ArrowPathIcon className="h-4 w-4" />
                  ) : (
                    <SparklesIcon className="h-4 w-4" />
                  )}
                  {subscription && hasActive ? 'Change Plan' : 'Subscribe Now'}
                </button>
              </div>

              <LogoutButton onClick={handleLogout} />
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col items-center text-center">
                <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-primary-100 text-2xl font-bold text-primary-700 ring-4 ring-primary-50">
                  {personalPhotoSrc && !personalImageFailed ? (
                    <img
                      src={personalPhotoSrc}
                      alt={fullName}
                      className="h-full w-full object-cover"
                      onError={() => setPersonalImageFailed(true)}
                    />
                  ) : (
                    initials
                  )}
                </div>
                {role === 'student' ? (
                  <button
                    type="button"
                    onClick={openStudentProfile}
                    className="mt-4 rounded-lg px-2 py-1 text-xl font-semibold text-gray-900 transition hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
                    title="Open full student profile"
                  >
                    {fullName}
                  </button>
                ) : (
                  <h3 className="mt-4 text-xl font-semibold text-gray-900">
                    {fullName}
                  </h3>
                )}
                <span className="mt-1 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                  {ROLE_LABELS[role] || role.replace(/_/g, ' ') || 'User'}
                </span>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white px-4 shadow-sm">
                <DetailRow icon={EnvelopeIcon} label="Email" value={user?.email} />
                <DetailRow
                  icon={PhoneIcon}
                  label="Phone number"
                  value={profile.phone || profile.phone_number || user?.phone || user?.phone_number}
                />
                {role === 'student' && (
                  <DetailRow
                    icon={AcademicCapIcon}
                    label="Class"
                    value={className || 'Not assigned'}
                  />
                )}
                {role === 'teacher' && (
                  <>
                    <DetailRow
                      icon={BriefcaseIcon}
                      label="Years of Experience"
                      value={`${yearsOfExperience} ${Number(yearsOfExperience) === 1 ? 'year' : 'years'}`}
                    />
                    <DetailRow
                      icon={UserGroupIcon}
                      label="Classes Taught"
                      value={
                        teacherClassNames.length
                          ? teacherClassNames.join(', ')
                          : 'No classes assigned'
                      }
                    />
                    <DetailRow
                      icon={AcademicCapIcon}
                      label="Class Teacher Of"
                      value={classTeacherClass?.name || 'Not assigned'}
                    />
                  </>
                )}
                {role === 'parent' && (
                  <DetailRow
                    icon={UserCircleIcon}
                    label="Relationship"
                    value={relationship}
                  />
                )}
              </div>

              <LogoutButton onClick={handleLogout} />
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

const LogoutButton = ({ onClick }) => (
  <div className="border-t border-gray-200 pt-4">
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600 transition hover:border-red-300 hover:bg-red-100 active:scale-[0.97]"
    >
      <ArrowRightOnRectangleIcon className="h-4 w-4" />
      Logout
    </button>
  </div>
);

export default ProfileDrawer;